import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { IWeissmanModalComponent } from '../../../Compliance/WeissmanModalService';
import { Constants } from '../../../constants.new';
import { TaxAuthorityBulkUpdateRequest } from '../../tax.rate.model';
import { Picklist } from '../../Tax-Authority/tax.authority.component';
import { AssessmentClassService } from '../../../Assessment-Class/assessmentClass.service';

import * as _ from 'lodash';
import moment from 'moment';

export interface TaxAuthorityBulkUpdateModalParams {
    selectedCount: number;
    selections: Core.TaxAuthorityToUpdate[];
}

export interface TriStateCheckboxList {
    label: string;
    value: any;
    checked?: boolean;
}

@Component({
    selector: 'tax-authority-bulk-update-modal',
    templateUrl: './tax.authority.bulk.update.component.html',
    standalone: false,
})
export class TaxAuthorityBulkUpdateComponent
    implements IWeissmanModalComponent<TaxAuthorityBulkUpdateModalParams, TaxAuthorityBulkUpdateRequest>, OnInit
{
    constructor(
        private bsModalRef: BsModalRef,
        public constants: Constants,
        private toastr: ToastrService,
        private readonly assessmentClassService: AssessmentClassService,
    ) {}

    params: TaxAuthorityBulkUpdateModalParams;
    result: TaxAuthorityBulkUpdateRequest;
    onClose: (request?: TaxAuthorityBulkUpdateRequest) => Promise<void> | void;
    loading: boolean = false;

    // Class members:
    headerLabel: string;
    taBulkUpdateRequest: TaxAuthorityBulkUpdateRequest;
    categories: Picklist[];
    taxableAssessments: Picklist[];
    certificationMonths: number[];
    certificationDays: number[];
    certificationTypes: Picklist[];
    selectableTaxYears: number[];
    defaultAnnualIncreaseDisplay: string;
    yoyPercentDisplay: string;
    availableExemptionComponents: Weissman.Model.Assessments.AssessmentComponent[];
    selectedTaxAuthorityExemptionComponents: Core.TaxAuthorityAssessmentComponentDTO[];
    allowedExemptions: TriStateCheckboxList[];
    selectedTaxAuthorityIds: number[];
    multipleStatesSelected: boolean = false;

    exemptions = {
        allExemptions: Core.TaxAuthorityExemptionTypeEnum.AllExemptions,
        certainExemptions: Core.TaxAuthorityExemptionTypeEnum.CertainExemptions,
        noExemptions: Core.TaxAuthorityExemptionTypeEnum.NoExemptions,
    };

    taxableAssessmentTypes = {
        totalAV: Core.TaxableAssessmentTypeEnum.TotalAV,
        alternateAV: Core.TaxableAssessmentTypeEnum.AlternateAV,
        landOnly: Core.TaxableAssessmentTypeEnum.LandOnly,
        lowerOfTotalAvAndAlternateAv: Core.TaxableAssessmentTypeEnum.LowerOfTotalAvAndAlternateAv,
    };

    async ngOnInit() {
        this.taBulkUpdateRequest = new TaxAuthorityBulkUpdateRequest();

        this.headerLabel = `Bulk Update ${this.params.selectedCount} Tax Authorities`;

        this.certificationMonths = _.range(1, 13);
        this.certificationDays = _.range(1, 32);

        this.taBulkUpdateRequest.allowExemptionsOnAlternateAV = null;

        const countOfTrueAllowExemptionsOnAlternateAVValues: number = this.params.selections.filter(
            (x) => x.allowExemptionsOnAlternateAV === true,
        ).length;

        if (countOfTrueAllowExemptionsOnAlternateAVValues === 0) {
            this.taBulkUpdateRequest.allowExemptionsOnAlternateAV = false;
        } else if (countOfTrueAllowExemptionsOnAlternateAVValues === this.params.selections.length) {
            this.taBulkUpdateRequest.allowExemptionsOnAlternateAV = true;
        }

        this.selectedTaxAuthorityIds = this.params.selections.map((x) => x.taxAuthorityId);

        this.multipleStatesSelected =
            this.params.selections.filter((item, i, arr) => arr.findIndex((t) => t.stateId === item.stateId) === i)
                .length > 1;

        if (this.multipleStatesSelected === false) {
            this.selectedTaxAuthorityExemptionComponents =
                await this.assessmentClassService.getExemptionSelectionsForTaxAuthorities(this.selectedTaxAuthorityIds);

            // A single state (e.g. Texas) must be selected for each set of grid rows, so all selected rows are for the same state:
            this.availableExemptionComponents = await this.assessmentClassService.getExemptionChoicesForTaxAuthority(
                this.params.selections[0].taxAuthorityId,
            );

            this.allowedExemptions = _.map(
                this.availableExemptionComponents,
                (exemption: Weissman.Model.Assessments.AssessmentComponent) => {
                    return {
                        value: exemption.assessmentComponentID,
                        label: exemption.componentName,
                    };
                },
            );

            this.availableExemptionComponents.forEach((component) => {
                const taExemptionCount: number = this.selectedTaxAuthorityExemptionComponents.filter(
                    (x) => x.assessmentComponentId === component.assessmentComponentID,
                ).length;

                const allowedExemption: TriStateCheckboxList = this.allowedExemptions.find((x) => {
                    return x.value === component.assessmentComponentID;
                });

                if (taExemptionCount === 0) {
                    allowedExemption.checked = false;
                } else if (taExemptionCount < this.selectedTaxAuthorityIds.length) {
                    allowedExemption.checked = null;
                } else if (taExemptionCount === this.selectedTaxAuthorityIds.length) {
                    allowedExemption.checked = true;
                } else {
                    this.toastr.info(`Error setting Exemptions Honored selections.`);
                }
            });
        }

        this.categories = _.map(this.constants.TaxAuthorityCategories, (names: any, id: number) => {
            return {
                label: names.displayName,
                id: Number(id),
            };
        });

        this.taxableAssessments = _.map(this.constants.TaxableAssessmentTypesForTrueRate, (names: any, id: number) => {
            return {
                id: Number(id),
                label: `${names.displayName} ${names.extraDisplay}`,
            };
        });

        this.certificationTypes = [
            {
                label: 'Prior Prior',
                id: -2,
            },
            {
                label: 'Prior',
                id: -1,
            },
            {
                label: 'Current',
                id: 0,
            },
            {
                label: 'Next',
                id: 1,
            },
            {
                label: 'Next Next',
                id: 2,
            },
        ];

        const currentYear: number = Number(moment().format('YYYY'));
        const years = _.range(currentYear - 10, currentYear + 6);
        this.selectableTaxYears = _.chain(years).reverse().value();
        this.taBulkUpdateRequest.taxYear = currentYear;

        this.defaultAnnualIncreaseDisplay = new Decimal(this.taBulkUpdateRequest.defaultAnnualIncrease)
            .times(100)
            .toFixed(4);
        this.yoyPercentDisplay = new Decimal(this.taBulkUpdateRequest.yoyPercent).times(100).toFixed(4);
    }

    isSaveable(): boolean {
        return (
            (this.taBulkUpdateRequest.categoryAction !== Core.CommonBulkUpdateFieldActionEnum.NoChange &&
                !!this.taBulkUpdateRequest.taxAuthorityCategoryId) ||
            this.taBulkUpdateRequest.taxableAssessmentTypeAction !== Core.CommonBulkUpdateFieldActionEnum.NoChange ||
            this.taBulkUpdateRequest.taxAuthorityExemptionTypeAction !==
                Core.CommonBulkUpdateFieldActionEnum.NoChange ||
            this.taBulkUpdateRequest.certificationDateAction !== Core.CommonBulkUpdateFieldActionEnum.NoChange ||
            this.taBulkUpdateRequest.obtainInAdvanceAction !== Core.CommonBulkUpdateFieldActionEnum.NoChange ||
            this.taBulkUpdateRequest.defaultAnnualIncreaseAction !== Core.CommonBulkUpdateFieldActionEnum.NoChange ||
            this.taBulkUpdateRequest.yoyAction !== Core.CommonBulkUpdateFieldActionEnum.NoChange
        );
    }

    defaultAnnualIncreaseBlur(): void {
        if (!this._isValidPercentage(this.defaultAnnualIncreaseDisplay)) {
            this.defaultAnnualIncreaseDisplay = '0.0';
        }
        this.defaultAnnualIncreaseDisplay = new Decimal(this.defaultAnnualIncreaseDisplay).toFixed(4);
    }

    yoyPercentBlur(): void {
        if (!this._isValidPercentage(this.yoyPercentDisplay)) {
            this.yoyPercentDisplay = '0.0';
        }
        this.yoyPercentDisplay = new Decimal(this.yoyPercentDisplay).toFixed(4);
    }

    async save(): Promise<void> {
        // Validate Category selected if ChangeTo.
        if (
            this.taBulkUpdateRequest.categoryAction === Core.CommonBulkUpdateFieldActionEnum.ChangeTo &&
            !_.some(this.categories, ['id', this.taBulkUpdateRequest.taxAuthorityCategoryId])
        ) {
            this.toastr.error("A Category must be selected for 'Change To' Action.", 'Cannot Save!');
            return;
        }

        // Validate all components of Certification Date selected if ChangeTo.
        if (
            this.taBulkUpdateRequest.certificationDateAction === Core.CommonBulkUpdateFieldActionEnum.ChangeTo &&
            (!this.taBulkUpdateRequest.certificationMonth ||
                !this.taBulkUpdateRequest.certificationDay ||
                !_.some(this.certificationTypes, ['id', this.taBulkUpdateRequest.certificationType]))
        ) {
            this.toastr.error(
                "All parts of Certification Date must be selected for 'Change To' Action.",
                'Cannot Save!',
            );
            return;
        }

        // Convert string percent values to numeric percent values.
        if (this.taBulkUpdateRequest.defaultAnnualIncreaseAction === Core.CommonBulkUpdateFieldActionEnum.ChangeTo) {
            this.taBulkUpdateRequest.defaultAnnualIncrease = new Decimal(this.defaultAnnualIncreaseDisplay)
                .dividedBy(100)
                .toNumber();
        }
        if (this.taBulkUpdateRequest.yoyAction === Core.CommonBulkUpdateFieldActionEnum.ChangeTo) {
            this.taBulkUpdateRequest.yoyPercent = new Decimal(this.yoyPercentDisplay).dividedBy(100).toNumber();
        }

        this.taBulkUpdateRequest.allowedExemptionAssessmentComponentSelections = [];

        if (this.allowedExemptions) {
            this.allowedExemptions.forEach((exemption) => {
                const exemptionSelection: Core.TaxAuthorityAssessmentComponentDTO = {
                    taxAuthorityId: null,
                    assessmentComponentId: exemption.value,
                    selected: exemption.checked,
                };
                this.taBulkUpdateRequest.allowedExemptionAssessmentComponentSelections.push(exemptionSelection);
            });
        }

        this.result = this.taBulkUpdateRequest;
        if (this.onClose) {
            await this.onClose(this.result);
        }
    }

    async cancel(): Promise<void> {
        this.bsModalRef.hide();
        if (this.onClose) {
            await this.onClose();
        }
    }

    isDisabled(action: Core.CommonBulkUpdateFieldActionEnum) {
        return action !== Core.CommonBulkUpdateFieldActionEnum.ChangeTo;
    }

    private _isValidPercentage(value: string | number): boolean {
        return !(this._isInvalidNumber(value) || isNaN(Number(value)));
    }

    private _isInvalidNumber(value: string | number): boolean {
        return value === null || value === undefined || value === '' || value === '.';
    }
}
